import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import CountUp from 'react-countup';
import {
  type Sentiment,
  sentimentText,
  sentimentIconBg,
  sentimentAccentBorder,
  sentimentTrendPill,
} from '@/lib/design-tokens';

interface MetricCardProps {
  label: string;
  value: string | number;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    value: string;
    text?: string;
  };
  icon?: React.ReactNode;
  sentiment?: Sentiment;
  subtitle?: string;
  animated?: boolean;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}

export function MetricCard({
  label,
  value,
  trend,
  icon,
  sentiment = 'neutral',
  subtitle,
  animated = false,
  prefix = '',
  suffix = '',
  decimals,
  className,
}: MetricCardProps) {
  // Generate a unique ID for accessibility
  const labelId = `metric-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;

  // Determine if value is numeric for animation
  const numericValue = typeof value === 'number' ? value : parseFloat(value.toString().replace(/[^0-9.-]/g, ''));
  const isNumeric = !isNaN(numericValue);
  const shouldAnimate = animated && isNumeric;

  // Auto-detect decimals if not provided
  const decimalPlaces = decimals !== undefined
    ? decimals
    : numericValue % 1 !== 0
    ? 1
    : 0;

  return (
    <Card
      role="region"
      aria-labelledby={labelId}
      className={cn(
        'bg-white border border-neutral-100 rounded-lg p-5 shadow-card',
        'min-h-[128px]',
        'border-l-2',
        sentimentAccentBorder[sentiment],
        'hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200',
        className
      )}
    >
      <div className="flex items-start justify-between mb-2">
        {/* Label — uppercase, smaller, wider tracking */}
        <span id={labelId} className="text-xs font-semibold uppercase text-neutral-400 tracking-wider">
          {label}
        </span>

        {/* Icon — slightly smaller container */}
        {icon && (
          <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center', sentimentIconBg[sentiment])}>
            {icon}
          </div>
        )}
      </div>

      {/* Value — reduced from text-4xl to text-3xl */}
      <div className={cn('text-3xl font-bold tracking-tight', sentimentText[sentiment])} aria-live="polite">
        {shouldAnimate ? (
          <>
            {prefix}
            <CountUp
              end={numericValue}
              decimals={decimalPlaces}
              duration={1.5}
              delay={0.2}
            />
            {suffix}
          </>
        ) : (
          <>
            {prefix}{value}{suffix}
          </>
        )}
      </div>

      {/* Subtitle or Trend */}
      <div className="flex items-center gap-2 mt-1.5">
        {trend ? (
          <div className="flex items-center gap-2">
            {/* Trend pill */}
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                sentimentTrendPill[trend.direction]
              )}
            >
              {trend.direction === 'up' && <TrendingUp className="h-3 w-3" />}
              {trend.direction === 'down' && <TrendingDown className="h-3 w-3" />}
              {trend.direction === 'neutral' && <Minus className="h-3 w-3" />}
              <span>{trend.value}</span>
            </span>
            {/* Comparison context */}
            {trend.text && (
              <span className="text-xs text-neutral-400">{trend.text}</span>
            )}
          </div>
        ) : subtitle ? (
          <span className="text-xs text-neutral-400">{subtitle}</span>
        ) : null}
      </div>
    </Card>
  );
}

// KPI Grid wrapper for consistent layout
interface KPIGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}

export function KPIGrid({ children, columns = 4, className }: KPIGridProps) {
  return (
    <div
      className={cn(
        'grid gap-5',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-1 md:grid-cols-2',
        columns === 3 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
        columns === 4 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        className
      )}
    >
      {children}
    </div>
  );
}

// Grouped KPI cards with dashed border (LognAI pattern)
interface GroupedKPIProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function GroupedKPI({ title, children, className }: GroupedKPIProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-dashed border-gray-300 dark:border-slate-700 p-4',
        className
      )}
    >
      {title && (
        <h3 className="mb-4 text-xs font-semibold uppercase text-muted-foreground tracking-wider">{title}</h3>
      )}
      {children}
    </div>
  );
}
